import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Search, ChevronRight, Trash2, DollarSign, FolderKanban, Pencil, CalendarDays, X, Check, MapPin, User, Building2, Archive, FileText, ExternalLink, Paperclip, Upload, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { ExpandableTextarea } from '@/components/ExpandableTextarea';
import { SearchableSelect } from '@/components/SearchableSelect';
import { FileUpload } from '@/components/FileUpload';
import { DocumentPreviewModal } from '@/components/DocumentPreviewModal';
import RichTextEditor from '@/components/RichTextEditor';
import IsolatedRichTextField from '@/components/IsolatedRichTextField';
import RichTextDisplay from '@/components/RichTextDisplay';
import { sortSections, groupSectionsByTag, SOURCE_TAG_OPTIONS } from '@/lib/sectionSort';
import api from '@/lib/api';

const EVENT_TYPES = [
  'Filing Expiration', 'Subfiling Expiration', 'License/Certificate Expiration',
  'Filing Due Date', 'Subfiling Due Date', 'Reminder', 'Other',
];

const statusColors = { Won: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', Lost: 'bg-destructive/10 text-destructive border-destructive/20', Direct: 'bg-primary/10 text-primary border-primary/20' };

const CONTRACT_JURISDICTION_OPTIONS = [
  { value: 'nyc', label: 'NYC' },
  { value: 'nassau_county', label: 'Nassau County' },
  { value: 'suffolk_county', label: 'Suffolk County' },
  { value: 'westchester', label: 'Westchester' },
  { value: 'other', label: 'Other' },
];

export default function ContractsPage() {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [clients, setClients] = useState([]);
  const [convertOpen, setConvertOpen] = useState(false);
  const [sectionChecks, setSectionChecks] = useState({});
  const [users, setUsers] = useState([]);
  const [sectionFormOpen, setSectionFormOpen] = useState(false);
  const [addendumFormOpen, setAddendumFormOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({ section_name: '', section_number: '', fee_amount: 0, notes: '', source_tag: 'Original Contract' });
  const [addendumForm, setAddendumForm] = useState({ section_name: '', section_number: '', fee: 0, notes: '', source_tag: '' });
  const [templates, setTemplates] = useState([]);
  const [pushOpen, setPushOpen] = useState(false);
  const [pushChecks, setPushChecks] = useState({});
  const [editSection, setEditSection] = useState(null);
  const [editSectionForm, setEditSectionForm] = useState({});
  const [eventTypePrompt, setEventTypePrompt] = useState(false);
  const [pendingEventType, setPendingEventType] = useState('');
  const [editContractNumber, setEditContractNumber] = useState(false);
  const [editContractNumberVal, setEditContractNumberVal] = useState('');
  const [editContractTitle, setEditContractTitle] = useState(false);
  const [editContractTitleVal, setEditContractTitleVal] = useState('');
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({});
  // Isolated ref for the Contract Notes RichTextEditor - keeps keystrokes
  // out of `infoForm` so they don't re-render the entire 1100-line page.
  const infoNotesRef = useRef(null);
  const [allClients, setAllClients] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [statusFilterC, setStatusFilterC] = useState('all');
  const [noProjectFilter, setNoProjectFilter] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  // Subconsultant agreements
  const [scaFormOpen, setScaFormOpen] = useState(false);
  const [scaForm, setScaForm] = useState({ name: '', subconsultant_name: '', notes: '' });
  const [scaUploadedFile, setScaUploadedFile] = useState(null);
  const [scaPreviewDoc, setScaPreviewDoc] = useState(null);
  const scaSavedRef = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchContracts = () => api.get('/contracts').then(r => setContracts(r.data));
  useEffect(() => {
    fetchContracts();
    api.get('/clients').then(r => { setClients(r.data); setAllClients(r.data || []); }).catch(() => {});
    api.get('/users').then(r => setUsers(r.data)).catch(() => {});
    api.get('/proposal-templates').then(r => setTemplates(r.data)).catch(() => {});
    api.get('/contacts').then(r => setAllContacts(r.data || [])).catch(() => {});
  }, []);

  // Deep-link: /contracts?contract=<id> auto-opens that contract's detail panel.
  useEffect(() => {
    const cid = searchParams.get('contract');
    if (!cid) return;
    api.get(`/contracts/${cid}`)
      .then(r => {
        setSelected(r.data);
        setEditContractNumber(false);
        setEditContractTitle(false);
        setDetailOpen(true);
      })
      .catch(() => toast.error('Contract not found'));
    const next = new URLSearchParams(searchParams);
    next.delete('contract');
    setSearchParams(next, { replace: true });
  }, []);

  const openDetail = async (c) => {
    const r = await api.get(`/contracts/${c.id}`);
    setSelected(r.data);
    setEditContractNumber(false);
    setEditContractTitle(false);
    setDetailOpen(true);
  };

  const refreshSelected = async () => {
    if (!selected) return;
    const r = await api.get(`/contracts/${selected.id}`);
    setSelected(r.data);
  };

  const saveContractNumber = async () => {
    const val = editContractNumberVal.trim();
    if (!val) return toast.error('Contract number cannot be empty');
    await api.put(`/contracts/${selected.id}`, { contract_number: val });
    setSelected(prev => ({ ...prev, contract_number: val }));
    setEditContractNumber(false);
    fetchContracts();
    toast.success('Contract number updated');
  };

  const saveContractTitle = async () => {
    const val = editContractTitleVal.trim();
    if (!val) return toast.error('Title cannot be empty');
    await api.put(`/contracts/${selected.id}`, { title: val });
    setSelected(prev => ({ ...prev, title: val }));
    setEditContractTitle(false);
    fetchContracts();
    toast.success('Title updated');
  };

  const openInfoEdit = () => {
    setInfoForm({
      client_id: selected.client_id || '',
      primary_contact_id: selected.primary_contact_id || '',
      billable_contact_id: selected.billable_contact_id || '',
      project_address: selected.project_address || '',
      project_manager_ids: selected.project_manager_ids || [],
      jurisdiction: Array.isArray(selected.jurisdiction) ? selected.jurisdiction : [],
      jurisdiction_other: selected.jurisdiction_other || '',
      notes: selected.notes || '',
    });
    setEditingInfo(true);
  };

  const saveInfoEdit = async () => {
    try {
      const payload = { ...infoForm, notes: infoNotesRef.current?.getValue() ?? infoForm.notes ?? '' };
      await api.put(`/contracts/${selected.id}`, payload);
      toast.success('Contract info updated');
      setEditingInfo(false);
      refreshSelected();
    } catch { toast.error('Failed to update'); }
  };

  const resetScaForm = () => {
    // If the user closes/cancels the form WITHOUT having saved an agreement,
    // clean up the orphan GridFS file. savedRef is set to true inside the
    // success path so this only fires on Cancel / sheet-dismiss flows.
    if (!scaSavedRef.current && scaUploadedFile?.file_id) {
      api.delete(`/upload/${scaUploadedFile.file_id}`).catch(() => {});
    }
    scaSavedRef.current = false;
    setScaForm({ name: '', subconsultant_name: '', notes: '' });
    setScaUploadedFile(null);
    setScaFormOpen(false);
  };

  const addSubconsultantAgreement = async () => {
    if (!scaForm.name?.trim()) return toast.error('Agreement name is required');
    if (!scaUploadedFile?.file_id) return toast.error('Please upload an agreement file');
    try {
      await api.post(`/contracts/${selected.id}/subconsultant-agreements`, {
        name: scaForm.name.trim(),
        subconsultant_name: scaForm.subconsultant_name?.trim() || '',
        notes: scaForm.notes?.trim() || '',
        file_id: scaUploadedFile.file_id,
        file_url: scaUploadedFile.file_url,
        file_name: scaUploadedFile.file_name,
        file_size: scaUploadedFile.file_size,
        content_type: scaUploadedFile.content_type,
      });
      toast.success('Subconsultant Agreement uploaded');
      scaSavedRef.current = true;
      resetScaForm();
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error uploading agreement'); }
  };

  const deleteSubconsultantAgreement = async (aid) => {
    if (!window.confirm('Remove this Subconsultant Agreement?')) return;
    try {
      await api.delete(`/contracts/${selected.id}/subconsultant-agreements/${aid}`);
      toast.success('Agreement removed');
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const addSection = async () => {
    if (!sectionForm.section_name) return toast.error('Section name required');
    try {
      await api.post(`/contracts/${selected.id}/sections`, sectionForm);
      toast.success('Section added');
      setSectionForm({ section_name: '', section_number: '', fee_amount: 0, notes: '', source_tag: 'Original Contract' });
      setSectionFormOpen(false);
      refreshSelected();
    } catch (e) { toast.error('Error adding section'); }
  };

  const addAddendum = async () => {
    if (!addendumForm.section_name) return toast.error('Addendum name required');
    try {
      await api.post(`/contracts/${selected.id}/addendums`, addendumForm);
      toast.success('Addendum added');
      setAddendumForm({ section_name: '', section_number: '', fee: 0, notes: '', source_tag: '' });
      setAddendumFormOpen(false);
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const deleteSection = async (sid) => {
    if (!window.confirm('Delete this section? This cannot be undone.')) return;
    try {
      await api.delete(`/contract-sections/${sid}`);
      toast.success('Section removed');
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot delete'); }
  };

  const openConvert = () => {
    const checks = {};
    (selected?.sections || []).forEach(s => { checks[s.id] = true; });
    setSectionChecks(checks);
    setConvertOpen(true);
  };

  const handleConvert = async () => {
    const section_ids = Object.entries(sectionChecks).filter(([_, v]) => v).map(([k]) => k);
    try {
      const r = await api.post(`/contracts/${selected.id}/convert-to-project`, { section_ids });
      toast.success(`Project ${r.data.project_number} created!`);
      setConvertOpen(false);
      setDetailOpen(false);
      fetchContracts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error converting'); }
  };

  const selectSectionTemplate = (tmpl, formSetter, currentForm) => {
    const t = templates.find(t => t.name === tmpl);
    if (t) formSetter({ ...currentForm, section_name: t.name, notes: t.scope_of_work || '' });
  };

  const openPushSections = () => {
    const checks = {};
    (selected?.sections || []).forEach(s => { if (!s.project_id) checks[s.id] = true; });
    setPushChecks(checks);
    setPushOpen(true);
  };

  const handlePush = async () => {
    const section_ids = Object.entries(pushChecks).filter(([_, v]) => v).map(([k]) => k);
    if (!section_ids.length) return toast.error('No sections selected');
    try {
      const r = await api.post(`/contracts/${selected.id}/push-sections`, { section_ids });
      toast.success(`${r.data.pushed} section(s) pushed to project`);
      setPushOpen(false);
      refreshSelected();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const openEditSection = (s) => {
    setEditSection(s);
    setEditSectionForm({
      section_name: s.section_name || '',
      section_number: s.section_number || '',
      fee_amount: s.fee_amount || 0,
      category: s.category || '',
      agency: s.agency || '',
      agency_status: s.agency_status || '',
      status: s.status || 'Not Started',
      notes: s.notes || '',
      source_tag: s.source_tag || 'Original Contract',
      reference_po_number: s.reference_po_number || '',
      start_date: s.start_date || '',
      due_date: s.due_date || '',
      approval_date: s.approval_date || '',
      expiration_date: s.expiration_date || '',
    });
    setSectionFormOpen(false);
    setAddendumFormOpen(false);
  };

  const saveEditSection = async (calendarEventType = null) => {
    if (!editSectionForm.section_name) return toast.error('Section name required');

    // Check if expiration_date is new or changed - prompt for event type
    const isNewExpiration = editSectionForm.expiration_date && editSectionForm.expiration_date !== (editSection?.expiration_date || '');
    if (isNewExpiration && calendarEventType === null && !eventTypePrompt) {
      setEventTypePrompt(true);
      setPendingEventType('Filing Expiration');
      return;
    }

    const payload = { ...editSectionForm };
    if (isNewExpiration && calendarEventType) {
      payload.calendar_event_type = calendarEventType;
    }

    try {
      await api.put(`/contract-sections/${editSection.id}`, payload);
      toast.success('Section updated');
      if (isNewExpiration && calendarEventType) {
        toast.success('Calendar event created');
      }
      const savedSectionId = editSection.id;
      setEditSection(null);
      setEventTypePrompt(false);
      setPendingEventType('');
      const r = await api.get(`/contracts/${selected.id}`);
      setSelected(r.data);
      // Re-open the just-saved section with fresh data so user can continue editing or click another
      const freshSection = (r.data.sections || []).find(s => s.id === savedSectionId);
      if (freshSection) setEditSection(null); // Collapse after save - user can freely click another section now
      fetchContracts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error updating section'); }
  };

  const confirmEventTypeAndSave = () => {
    setEventTypePrompt(false);
    saveEditSection(pendingEventType);
  };

  const skipEventTypeAndSave = () => {
    setEventTypePrompt(false);
    saveEditSection('');
  };

  const filtered = contracts.filter(c => {
    const q = search.toLowerCase();
    const matchesSearch = !q
      || (c.contract_number || '').toLowerCase().includes(q)
      || (c.title || '').toLowerCase().includes(q)
      || (c.client_name || '').toLowerCase().includes(q);
    const matchesStatus = statusFilterC === 'all' || c.status === statusFilterC;
    const matchesNoProject = !noProjectFilter || !c.project_id;
    return matchesSearch && matchesStatus && matchesNoProject;
  });

  const hasActiveFilters = search || statusFilterC !== 'all' || noProjectFilter;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="contracts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="flex items-baseline gap-3 text-[22px] font-medium tracking-tight">Contracts<span className="text-sm text-muted-foreground/70 font-normal tabular-nums">{contracts.length}</span></h1>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs gap-1.5 border-border" onClick={() => navigate('/contracts/inactive')} data-testid="view-inactive-btn">
            <Archive className="h-3.5 w-3.5" /> View Inactive
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Search by contract #, title, or client..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9 bg-card border-border w-64" data-testid="contracts-search" />
        </div>
        <Select value={statusFilterC} onValueChange={setStatusFilterC}>
          <SelectTrigger className="w-36 bg-card border-border text-xs h-9" data-testid="contracts-status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all" className="text-xs">All Statuses</SelectItem>
            <SelectItem value="Won" className="text-xs">Won</SelectItem>
            <SelectItem value="Lost" className="text-xs">Lost</SelectItem>
            <SelectItem value="Direct" className="text-xs">Direct</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant={noProjectFilter ? 'default' : 'outline'}
          size="sm"
          className={`text-xs h-9 ${noProjectFilter ? 'bg-primary text-primary-foreground' : 'border-border'}`}
          onClick={() => setNoProjectFilter(!noProjectFilter)}
          data-testid="no-project-filter"
        >
          No Project Linked
        </Button>
        {hasActiveFilters && (
          <button
            className="text-xs text-primary hover:underline ml-1"
            onClick={() => { setSearch(''); setStatusFilterC('all'); setNoProjectFilter(false); }}
            data-testid="clear-contract-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="border border-border rounded-md bg-card">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Number</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Title</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Status</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Total Fees</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground">Project</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-muted-foreground text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(c => (
              <TableRow key={c.id} className="border-border hover:bg-secondary/30 cursor-pointer" onClick={() => openDetail(c)} data-testid={`contract-row-${c.id}`}>
                <TableCell className="font-medium text-sm">{c.contract_number}</TableCell>
                <TableCell className="text-sm">{c.title}</TableCell>
                <TableCell><Badge className={`text-xs ${statusColors[c.status] || ''}`}>{c.status}</Badge></TableCell>
                <TableCell className="text-sm font-heading font-bold">${(c.total_fees || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                <TableCell>{c.project_id ? <Badge variant="outline" className="text-xs text-emerald-400">Linked</Badge> : '-'}</TableCell>
                <TableCell className="text-right"><ChevronRight className="h-4 w-4 text-muted-foreground inline" /></TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No contracts found</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={v => {
        // When the contract detail sheet closes with the SCA form mid-flight, clean
        // up any orphan upload so we don't leave files in GridFS.
        if (!v && scaFormOpen) {
          resetScaForm();
        }
        setDetailOpen(v);
      }}>
        <SheetContent className="sm:max-w-2xl overflow-y-auto" data-testid="contract-detail-sheet">
          <SheetHeader className="space-y-1.5">
            {/* Contract ID — 13px DM Mono muted above title */}
            {editContractNumber ? (
              <div className="flex items-center gap-1.5">
                <Input value={editContractNumberVal} onChange={e => setEditContractNumberVal(e.target.value)} className="h-7 w-40 text-sm font-mono bg-background" autoFocus onKeyDown={e => { if (e.key === 'Enter') saveContractNumber(); if (e.key === 'Escape') setEditContractNumber(false); }} data-testid="edit-contract-number-input" />
                <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-400" onClick={saveContractNumber}><Check className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => setEditContractNumber(false)}><X className="h-3.5 w-3.5" /></Button>
              </div>
            ) : (
              <p className="text-sm font-mono text-muted-foreground cursor-pointer hover:text-primary transition-colors w-fit"
                 onClick={() => { setEditContractNumber(true); setEditContractNumberVal(selected?.contract_number || ''); }}
                 data-testid="contract-number-editable">{selected?.contract_number}</p>
            )}
            {/* Title — 18px weight 500 */}
            <SheetTitle className="text-[18px] font-medium leading-tight">
              {editContractTitle ? (
                <span className="flex items-center gap-1.5">
                  <Input value={editContractTitleVal} onChange={e => setEditContractTitleVal(e.target.value)} className="h-8 text-[18px] font-medium bg-background" autoFocus onKeyDown={e => { if (e.key === 'Enter') saveContractTitle(); if (e.key === 'Escape') setEditContractTitle(false); }} data-testid="edit-contract-title-input" />
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-400" onClick={saveContractTitle}><Check className="h-3 w-3" /></Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => setEditContractTitle(false)}><X className="h-3 w-3" /></Button>
                </span>
              ) : (
                <span className="cursor-pointer hover:text-primary transition-colors"
                      onClick={() => { setEditContractTitle(true); setEditContractTitleVal(selected?.title || ''); }}
                      data-testid="contract-title-editable">{selected?.title}</span>
              )}
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">{selected?.client_name}</SheetDescription>
          </SheetHeader>
          {selected && (
            <div className="mt-4 space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={`${statusColors[selected.status] || ''}`}>{selected.status}</Badge>
                <span className="text-lg font-heading font-bold text-primary">${(selected.total_fees || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              {(selected.billed_to_date != null || selected.remaining_balance != null) && (
                <div className="flex items-center gap-4 text-xs">
                  <div><span className="text-muted-foreground">Billed to Date:</span> <span className="font-medium text-foreground">${(selected.billed_to_date || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                  <div><span className="text-muted-foreground">Remaining:</span> <span className={`font-medium ${(selected.remaining_balance || 0) < 0 ? 'text-destructive' : 'text-emerald-400'}`}>${(selected.remaining_balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                </div>
              )}

              {!selected.project_id && selected.status !== 'Lost' && (
                <Button onClick={openConvert} className="bg-emerald-500 text-white hover:bg-emerald-600" data-testid="convert-to-project-btn">
                  <FolderKanban className="h-4 w-4 mr-1" /> Convert to Project
                </Button>
              )}
              {selected.project_id && (
                <div className="flex items-center gap-2">
                  <RouterLink
                    to={`/projects/${selected.project_id}`}
                    className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 hover:underline border border-emerald-500/20 bg-emerald-500/5 rounded-md px-2 py-0.5"
                    data-testid="contract-view-project-link"
                  >
                    <ExternalLink className="h-3 w-3" />
                    {selected.project_number ? `View Project ${selected.project_number}` : 'View linked project'}
                  </RouterLink>
                  {(selected.sections || []).some(s => !s.project_id) && (
                    <Button size="sm" variant="outline" onClick={openPushSections} className="text-xs text-primary border-primary/30" data-testid="push-sections-btn">
                      <Plus className="h-3 w-3 mr-1" /> Push Sections to Project
                    </Button>
                  )}
                </div>
              )}

              {/* Mark Inactive - hidden if already inactive */}
              {selected.is_active !== false && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs text-zinc-400 border-zinc-500/30 hover:bg-zinc-500/10 gap-1"
                  onClick={() => { setDeactivateReason(''); setDeactivateOpen(true); }}
                  data-testid="mark-inactive-btn"
                >
                  <Archive className="h-3 w-3" /> Mark Inactive
                </Button>
              )}

              {/* Contract Info - Client, Contacts, Address */}
              <div className="border border-border rounded-md p-3 space-y-2" data-testid="contract-info-block">
                <div className="flex items-center justify-between">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contract Information</h3>
                  {!editingInfo ? (
                    <Button size="sm" variant="ghost" className="text-xs h-6 text-muted-foreground" onClick={openInfoEdit} data-testid="edit-contract-info-btn">
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                  ) : (
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="text-xs h-6 text-emerald-400" onClick={saveInfoEdit} data-testid="save-contract-info-btn">
                        <Check className="h-3 w-3 mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="ghost" className="text-xs h-6 text-muted-foreground" onClick={() => setEditingInfo(false)}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>

                {editingInfo ? (
                  <div className="space-y-3">
                    {/* Project Address (moved to top) */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Project Address</Label>
                      <Input value={infoForm.project_address} onChange={e => setInfoForm(f => ({ ...f, project_address: e.target.value }))} className="bg-background border-border h-8 text-sm" data-testid="info-project-address" />
                    </div>

                    {/* Client Selector */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Client</Label>
                      <SearchableSelect
                        value={infoForm.client_id || ''}
                        onValueChange={v => setInfoForm(f => ({ ...f, client_id: v }))}
                        options={[{ value: '', label: 'No client' }, ...allClients.map(c => ({ value: c.id, label: c.name }))]}
                        placeholder="Select a client..."
                        searchPlaceholder="Search clients..."
                        data-testid="info-client-select"
                      />
                    </div>

                    {/* Primary Contact Selector */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Primary Contact</Label>
                      <SearchableSelect
                        value={infoForm.primary_contact_id || ''}
                        onValueChange={v => setInfoForm(f => ({ ...f, primary_contact_id: v }))}
                        options={[{ value: '', label: 'No contact' }, ...allContacts.map(c => ({ value: c.id, label: `${c.name}${c.email ? ` (${c.email})` : ''}` }))]}
                        placeholder="Select primary contact..."
                        searchPlaceholder="Search contacts..."
                        data-testid="info-primary-contact-select"
                      />
                    </div>

                    {/* Billable Contact Selector */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Billable Contact</Label>
                      <SearchableSelect
                        value={infoForm.billable_contact_id || ''}
                        onValueChange={v => setInfoForm(f => ({ ...f, billable_contact_id: v }))}
                        options={[{ value: '', label: 'No contact' }, ...allContacts.map(c => ({ value: c.id, label: `${c.name}${c.email ? ` (${c.email})` : ''}` }))]}
                        placeholder="Select billable contact..."
                        searchPlaceholder="Search contacts..."
                        data-testid="info-billable-contact-select"
                      />
                    </div>

                    {/* Project Manager(s) Multi-select */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Project Manager(s)</Label>
                      <div className="space-y-1.5">
                        {(infoForm.project_manager_ids || []).map(pmId => {
                          const emp = users.find(u => u.id === pmId);
                          return (
                            <div key={pmId} className="flex items-center gap-1.5 text-sm">
                              <Badge variant="outline" className="text-xs">{emp?.name || pmId}</Badge>
                              <Button size="icon" variant="ghost" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => setInfoForm(f => ({ ...f, project_manager_ids: (f.project_manager_ids || []).filter(id => id !== pmId) }))} data-testid={`remove-pm-${pmId}`}>
                                <X className="h-3 w-3" />
                              </Button>
                            </div>
                          );
                        })}
                        <Select value="" onValueChange={v => { if (v && !(infoForm.project_manager_ids || []).includes(v)) setInfoForm(f => ({ ...f, project_manager_ids: [...(f.project_manager_ids || []), v] })); }}>
                          <SelectTrigger className="bg-background border-border h-8 text-xs" data-testid="add-pm-select">
                            <SelectValue placeholder="Add project manager..." />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            {users.filter(u => u.role !== 'Client' && !(infoForm.project_manager_ids || []).includes(u.id)).map(u => (
                              <SelectItem key={u.id} value={u.id} className="text-xs">{u.name} ({u.role})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Jurisdiction Multi-select (carried from proposal; freely editable) */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Jurisdiction</Label>
                      <div className="flex flex-wrap gap-1.5 mt-1" data-testid="contract-jurisdiction-edit">
                        {CONTRACT_JURISDICTION_OPTIONS.map(opt => {
                          const sel = (Array.isArray(infoForm.jurisdiction) ? infoForm.jurisdiction : []).includes(opt.value);
                          return (
                            <button key={opt.value} type="button"
                              onClick={() => setInfoForm(f => {
                                const cur = Array.isArray(f.jurisdiction) ? f.jurisdiction : [];
                                const next = cur.includes(opt.value) ? cur.filter(v => v !== opt.value) : [...cur, opt.value];
                                return { ...f, jurisdiction: next };
                              })}
                              className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${sel ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border hover:border-primary/50'}`}
                              data-testid={`contract-jurisdiction-${opt.value}`}>
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      {(Array.isArray(infoForm.jurisdiction) ? infoForm.jurisdiction : []).includes('other') && (
                        <Input value={infoForm.jurisdiction_other || ''} onChange={e => setInfoForm(f => ({ ...f, jurisdiction_other: e.target.value }))} placeholder="Specify other jurisdiction" className="bg-background border-border mt-1.5 h-8 text-xs" data-testid="contract-jurisdiction-other" />
                      )}
                    </div>

                    {/* Notes */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Notes</Label>
                      <IsolatedRichTextField ref={infoNotesRef} initialValue={infoForm.notes || ''} resetKey={selected?.id || 'new'} placeholder="Contract notes..." collapsedHeight={80} data-testid="info-notes-editor" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Project Address display (moved to top) */}
                    {selected.project_address && (
                      <div className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        <p className="text-sm text-muted-foreground">{selected.project_address}</p>
                      </div>
                    )}

                    {/* Client display */}
                    {selected.client_name ? (
                      <div className="flex items-start gap-2">
                        <Building2 className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <p className="text-sm font-medium">{selected.client_name}</p>
                          <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                            {selected.client_email && <span>{selected.client_email}</span>}
                            {selected.client_phone && <span>{selected.client_phone}</span>}
                            {selected.client_address && <span>{selected.client_address}</span>}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Building2 className="h-3 w-3" /> No client linked</p>
                    )}

                    {/* Primary Contact display */}
                    <div className="flex items-start gap-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Primary Contact</p>
                        {selected.primary_contact_name ? (
                          <div>
                            <p className="text-sm">{selected.primary_contact_name}</p>
                            <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                              {selected.primary_contact_email && <span>{selected.primary_contact_email}</span>}
                              {selected.primary_contact_phone && <span>{selected.primary_contact_phone}</span>}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">Not assigned</p>
                        )}
                      </div>
                    </div>

                    {/* Billable Contact display */}
                    <div className="flex items-start gap-2">
                      <DollarSign className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Billable Contact</p>
                        {selected.billable_contact_name ? (
                          <div>
                            <p className="text-sm">{selected.billable_contact_name}</p>
                            <div className="flex flex-wrap gap-x-3 text-xs text-muted-foreground">
                              {selected.billable_contact_email && <span>{selected.billable_contact_email}</span>}
                              {selected.billable_contact_phone && <span>{selected.billable_contact_phone}</span>}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">Not assigned</p>
                        )}
                      </div>
                    </div>

                    {/* Project Manager(s) display */}
                    <div className="flex items-start gap-2">
                      <User className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Project Manager(s)</p>
                        {(selected.project_manager_ids || []).length > 0 ? (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {(selected.project_manager_ids || []).map(pmId => (
                              <Badge key={pmId} variant="outline" className="text-xs">{(selected.project_manager_names || {})[pmId] || pmId}</Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground">Not assigned</p>
                        )}
                      </div>
                    </div>

                    {/* Jurisdiction display */}
                    <div className="flex items-start gap-2">
                      <MapPin className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Jurisdiction</p>
                        {(() => {
                          const j = selected.jurisdiction;
                          const arr = Array.isArray(j) ? j : (j ? [j] : []);
                          if (arr.length === 0) return <p className="text-xs text-muted-foreground">Not set</p>;
                          return (
                            <div className="flex flex-wrap gap-1 mt-0.5" data-testid="contract-jurisdiction-display">
                              {arr.map(v => {
                                const opt = CONTRACT_JURISDICTION_OPTIONS.find(o => o.value === v);
                                const label = opt ? opt.label : v;
                                return <Badge key={v} variant="outline" className="text-xs">{label}{v === 'other' && selected.jurisdiction_other ? `: ${selected.jurisdiction_other}` : ''}</Badge>;
                              })}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Notes display */}
                    {selected.notes && (
                      <div className="flex items-start gap-2">
                        <FileText className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground">Notes</p>
                          <div className="text-sm mt-0.5 overflow-hidden break-words" data-testid="contract-notes-display">
                            <RichTextDisplay value={selected.notes} />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sections ({(selected.sections || []).length})</h3>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => { setSectionFormOpen(!sectionFormOpen); setAddendumFormOpen(false); }} className="text-xs h-7" data-testid="add-section-btn"><Plus className="h-3 w-3 mr-1" /> Section</Button>
                    {selected.status !== 'Lost' && <Button size="sm" variant="outline" onClick={() => { setAddendumFormOpen(!addendumFormOpen); setSectionFormOpen(false); }} className="text-xs h-7" data-testid="add-addendum-btn"><Plus className="h-3 w-3 mr-1" /> Addendum</Button>}
                  </div>
                </div>

                {/* Inline Add Section Form */}
                {sectionFormOpen && (
                  <Card className="border-border border-dashed bg-transparent shadow-none mb-3">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground">New Section</p>
                      <SearchableSelect
                        value={sectionForm.section_name}
                        onValueChange={v => selectSectionTemplate(v, setSectionForm, sectionForm)}
                        options={templates.map(t => ({ value: t.name, label: `${t.name} (${t.jurisdiction_group})` }))}
                        placeholder="Select from template..."
                        searchPlaceholder="Search templates..."
                        data-testid="contract-section-template"
                      />
                      <Input placeholder="Or custom section name *" value={sectionForm.section_name} onChange={e => setSectionForm({ ...sectionForm, section_name: e.target.value })} className="bg-background border-border" data-testid="contract-section-name" />
                      <Input placeholder="Section # (as on contract document)" value={sectionForm.section_number} onChange={e => setSectionForm({ ...sectionForm, section_number: e.target.value })} className="bg-background border-border" data-testid="contract-section-number" />
                      <div>
                        <Label className="text-xs text-muted-foreground">Source Tag</Label>
                        <Select value={sectionForm.source_tag} onValueChange={v => setSectionForm({ ...sectionForm, source_tag: v })}>
                          <SelectTrigger className="bg-background border-border" data-testid="contract-section-source-tag"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-card border-border">{SOURCE_TAG_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Input type="number" placeholder="Fee amount ($)" value={sectionForm.fee_amount || ''} onChange={e => setSectionForm({ ...sectionForm, fee_amount: parseFloat(e.target.value) || 0 })} className="bg-background border-border" data-testid="contract-section-fee" />
                      <RichTextEditor placeholder="Notes (optional)" value={sectionForm.notes} onChange={v => setSectionForm(prev => ({ ...prev, notes: v }))} collapsedHeight={80} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={addSection} className="bg-primary text-primary-foreground" data-testid="contract-section-submit"><Plus className="h-3 w-3 mr-1" /> Add Section</Button>
                        <Button size="sm" variant="outline" onClick={() => setSectionFormOpen(false)}>Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Inline Add Addendum Form */}
                {addendumFormOpen && (
                  <Card className="border-amber-500/20 border-dashed bg-amber-500/5 shadow-none mb-3">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-amber-400">New Addendum</p>
                      <SearchableSelect
                        value={addendumForm.section_name}
                        onValueChange={v => selectSectionTemplate(v, setAddendumForm, addendumForm)}
                        options={templates.map(t => ({ value: t.name, label: `${t.name} (${t.jurisdiction_group})` }))}
                        placeholder="Select from template..."
                        searchPlaceholder="Search templates..."
                        data-testid="contract-addendum-template"
                      />
                      <Input placeholder="Or custom addendum name *" value={addendumForm.section_name} onChange={e => setAddendumForm({ ...addendumForm, section_name: e.target.value })} className="bg-background border-border" data-testid="contract-addendum-name" />
                      <Input placeholder="Section # (as on contract document)" value={addendumForm.section_number} onChange={e => setAddendumForm({ ...addendumForm, section_number: e.target.value })} className="bg-background border-border" data-testid="contract-addendum-number" />
                      <div>
                        <Label className="text-xs text-muted-foreground">Source Tag</Label>
                        <Select value={addendumForm.source_tag || `Addendum ${((selected?.addendums || []).length + 1)}`} onValueChange={v => setAddendumForm({ ...addendumForm, source_tag: v })}>
                          <SelectTrigger className="bg-background border-border" data-testid="contract-addendum-source-tag"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-card border-border">{SOURCE_TAG_OPTIONS.filter(t => t !== 'Original Contract').map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Input type="number" placeholder="Fee amount ($)" value={addendumForm.fee || ''} onChange={e => setAddendumForm({ ...addendumForm, fee: parseFloat(e.target.value) || 0 })} className="bg-background border-border" data-testid="contract-addendum-fee" />
                      <RichTextEditor placeholder="Notes (optional)" value={addendumForm.notes} onChange={v => setAddendumForm(prev => ({ ...prev, notes: v }))} collapsedHeight={80} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={addAddendum} className="bg-amber-500 text-white hover:bg-amber-600" data-testid="contract-addendum-submit"><Plus className="h-3 w-3 mr-1" /> Add Addendum</Button>
                        <Button size="sm" variant="outline" onClick={() => setAddendumFormOpen(false)}>Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Inline Edit Section Form */}
                {editSection && (
                  <Card className="border-primary/30 border-dashed bg-primary/5 shadow-none mb-3">
                    <CardContent className="p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-primary">Edit Section</p>
                        <p className="text-xs text-muted-foreground">Source: {editSection.section_origin || 'N/A'}</p>
                      </div>
                      {editSection.is_default ? (
                        <div className="flex items-center gap-2 py-1">
                          <Badge variant="outline" className="text-xs">Default (Reimbursables)</Badge>
                          <span className="text-xs text-muted-foreground">Name &amp; fee locked. Managed via Invoicing</span>
                        </div>
                      ) : (
                        <>
                          <Input placeholder="Section name *" value={editSectionForm.section_name} onChange={e => setEditSectionForm({ ...editSectionForm, section_name: e.target.value })} className="bg-background border-border" data-testid="edit-section-name" />
                          <Input type="number" placeholder="Fee amount ($)" value={editSectionForm.fee_amount || ''} onChange={e => setEditSectionForm({ ...editSectionForm, fee_amount: parseFloat(e.target.value) || 0 })} className="bg-background border-border" data-testid="edit-section-fee" />
                        </>
                      )}
                      <Input placeholder="Section #" value={editSectionForm.section_number} onChange={e => setEditSectionForm({ ...editSectionForm, section_number: e.target.value })} className="bg-background border-border" data-testid="edit-section-number" />
                      <div>
                        <Label className="text-xs text-muted-foreground">Source Tag</Label>
                        <Select value={editSectionForm.source_tag || 'Original Contract'} onValueChange={v => setEditSectionForm({ ...editSectionForm, source_tag: v })}>
                          <SelectTrigger className="bg-background border-border" data-testid="edit-section-source-tag"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-card border-border">{SOURCE_TAG_OPTIONS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Status</Label>
                          <Select value={editSectionForm.status} onValueChange={v => setEditSectionForm({ ...editSectionForm, status: v })}>
                            <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-card border-border">{['Not Started', 'In Progress', 'Completed', 'Cancelled'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Category</Label>
                          <Input value={editSectionForm.category} onChange={e => setEditSectionForm({ ...editSectionForm, category: e.target.value })} className="bg-background border-border" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-muted-foreground">Agency</Label>
                          <Input value={editSectionForm.agency} onChange={e => setEditSectionForm({ ...editSectionForm, agency: e.target.value })} className="bg-background border-border" />
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Agency Status</Label>
                          <Input value={editSectionForm.agency_status} onChange={e => setEditSectionForm({ ...editSectionForm, agency_status: e.target.value })} className="bg-background border-border" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">PO / Reference Number</Label>
                        <Input placeholder="PO or reference #" value={editSectionForm.reference_po_number} onChange={e => setEditSectionForm({ ...editSectionForm, reference_po_number: e.target.value })} className="bg-background border-border" data-testid="edit-section-po" />
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div><Label className="text-xs text-muted-foreground">Start Date</Label><Input type="date" value={editSectionForm.start_date || ''} onChange={e => setEditSectionForm({ ...editSectionForm, start_date: e.target.value })} className="bg-background border-border" /></div>
                        <div><Label className="text-xs text-muted-foreground">Due Date</Label><Input type="date" value={editSectionForm.due_date || ''} onChange={e => setEditSectionForm({ ...editSectionForm, due_date: e.target.value })} className="bg-background border-border" /></div>
                        <div><Label className="text-xs text-muted-foreground">Approval Date</Label><Input type="date" value={editSectionForm.approval_date || ''} onChange={e => setEditSectionForm({ ...editSectionForm, approval_date: e.target.value })} className="bg-background border-border" data-testid="edit-section-approval-date" /></div>
                        <div><Label className="text-xs text-muted-foreground">Expiration</Label><Input type="date" value={editSectionForm.expiration_date || ''} onChange={e => setEditSectionForm({ ...editSectionForm, expiration_date: e.target.value })} className="bg-background border-border" data-testid="edit-section-expiration" /></div>
                      </div>
                      <RichTextEditor placeholder="Notes" value={editSectionForm.notes} onChange={v => setEditSectionForm({ ...editSectionForm, notes: v })} collapsedHeight={80} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => saveEditSection()} className="bg-primary text-primary-foreground" data-testid="save-edit-section-btn">Save Changes</Button>
                        <Button size="sm" variant="outline" onClick={() => { setEditSection(null); setEventTypePrompt(false); }}>Cancel</Button>
                      </div>

                      {/* Event Type Selection Prompt */}
                      {eventTypePrompt && (
                        <Card className="border-amber-500/30 bg-amber-500/5 shadow-none mt-2">
                          <CardContent className="p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <CalendarDays className="h-4 w-4 text-amber-400" />
                              <p className="text-xs font-semibold text-amber-400">Calendar Event Type</p>
                            </div>
                            <p className="text-xs text-muted-foreground">A calendar event will be created for this expiration date. Select the event type:</p>
                            <Select value={pendingEventType} onValueChange={setPendingEventType}>
                              <SelectTrigger className="bg-background border-border" data-testid="expiration-event-type-select">
                                <SelectValue placeholder="Select event type" />
                              </SelectTrigger>
                              <SelectContent className="bg-card border-border">
                                {EVENT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <div className="flex gap-2">
                              <Button size="sm" onClick={confirmEventTypeAndSave} className="bg-amber-500 text-white hover:bg-amber-600" data-testid="confirm-event-type-btn">
                                <CalendarDays className="h-3 w-3 mr-1" /> Save & Create Event
                              </Button>
                              <Button size="sm" variant="outline" onClick={skipEventTypeAndSave} className="text-xs text-muted-foreground">
                                Skip (set later)
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </CardContent>
                  </Card>
                )}

                {groupSectionsByTag(selected.sections || []).map(group => (
                  <div key={group.tag} className="mb-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5 px-1">{group.tag}</p>
                    {group.sections.map(s => (
                      <Card key={s.id} className="border-border bg-background shadow-none mb-2 cursor-pointer hover:border-primary/30 transition-colors" onClick={() => openEditSection(s)} data-testid={`contract-section-${s.id}`}>
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                {s.section_number && <Badge variant="outline" className="text-xs font-mono">{s.section_number}</Badge>}
                                <p className="text-sm font-medium">{s.section_name}</p>
                                {s.is_default && <Badge variant="outline" className="text-xs">Default</Badge>}
                                <Badge variant="outline" className="text-xs">{s.status}</Badge>
                                {s.project_id && <Badge variant="outline" className="text-xs text-emerald-400 border-emerald-500/20">In Project</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {s.section_origin}
                                {s.reference_po_number && <span className="ml-2">PO: {s.reference_po_number}</span>}
                                {s.approval_date && <span className="ml-2">Approved: {s.approval_date}</span>}
                                {s.expiration_date && <span className="ml-2">Exp: {s.expiration_date}</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-heading font-bold text-primary">${(s.fee_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              <Pencil className="h-3 w-3 text-muted-foreground" />
                              {!s.is_default && <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={(e) => { e.stopPropagation(); deleteSection(s.id); }}><Trash2 className="h-3 w-3" /></Button>}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                ))}
              </div>

              {(selected.addendums || []).length > 0 && (
                <div className="border-t border-border pt-4">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Addendums</h3>
                  {selected.addendums.map(a => (
                    <div key={a.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                      <div><p className="text-sm">{a.section_name}</p><p className="text-xs text-muted-foreground">{a.date_added}</p></div>
                      <span className="text-sm font-heading font-bold">${(a.fee || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Subconsultant Agreements */}
              <div className="border-t border-border pt-4" data-testid="subconsultant-agreements-section">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Subconsultant Agreements</h3>
                    <p className="text-xs text-muted-foreground/80 mt-0.5">Upload signed agreements so PMs can reference payment terms inside the portal.</p>
                  </div>
                  {!scaFormOpen && (
                    <Button size="sm" variant="outline" onClick={() => setScaFormOpen(true)} className="text-xs h-7" data-testid="add-subconsultant-agreement-btn">
                      <Upload className="h-3 w-3 mr-1" /> Upload Agreement
                    </Button>
                  )}
                </div>
                {scaFormOpen && (
                  <Card className="border-primary/20 bg-primary/5 shadow-none mb-3">
                    <CardContent className="p-3 space-y-2">
                      <p className="text-xs font-semibold text-primary">New Subconsultant Agreement</p>
                      <div>
                        <Label className="text-xs text-muted-foreground">Agreement Name *</Label>
                        <Input
                          value={scaForm.name}
                          onChange={e => setScaForm({ ...scaForm, name: e.target.value })}
                          className="bg-background border-border h-8 text-sm"
                          placeholder="e.g. Acme MEP Subconsultant Agreement"
                          data-testid="sca-name"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Subconsultant Company (optional)</Label>
                        <Input
                          value={scaForm.subconsultant_name}
                          onChange={e => setScaForm({ ...scaForm, subconsultant_name: e.target.value })}
                          className="bg-background border-border h-8 text-sm"
                          placeholder="e.g. Acme MEP Inc."
                          data-testid="sca-subconsultant"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
                        <Input
                          value={scaForm.notes}
                          onChange={e => setScaForm({ ...scaForm, notes: e.target.value })}
                          className="bg-background border-border h-8 text-sm"
                          placeholder="Payment terms summary..."
                          data-testid="sca-notes"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Document *</Label>
                        {scaUploadedFile ? (
                          <div className="flex items-center gap-2 p-2 border border-emerald-500/20 rounded-md bg-emerald-500/5 mt-1">
                            <Paperclip className="h-4 w-4 text-emerald-400 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium truncate">{scaUploadedFile.file_name}</p>
                              <p className="text-xs text-muted-foreground">{(scaUploadedFile.file_size / 1024).toFixed(0)} KB</p>
                            </div>
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { api.delete(`/upload/${scaUploadedFile.file_id}`).catch(() => {}); setScaUploadedFile(null); }}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        ) : (
                          <FileUpload onUploadComplete={setScaUploadedFile} className="mt-1" />
                        )}
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" onClick={addSubconsultantAgreement} className="bg-primary text-primary-foreground gap-1" data-testid="sca-submit">
                          <Check className="h-3 w-3" /> Save Agreement
                        </Button>
                        <Button size="sm" variant="outline" onClick={resetScaForm} className="gap-1"><X className="h-3 w-3" /> Cancel</Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {(selected.subconsultant_agreements || []).length === 0 && !scaFormOpen && (
                  <p className="text-xs text-muted-foreground italic py-2">No subconsultant agreements uploaded yet.</p>
                )}
                {(selected.subconsultant_agreements || []).map(a => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 py-2 border-b border-border last:border-0 hover:bg-secondary/20 transition-colors px-1 rounded"
                    data-testid={`subconsultant-agreement-${a.id}`}
                  >
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setScaPreviewDoc(a)}>
                      <p className="text-sm font-medium truncate">{a.name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {a.subconsultant_name && <span>{a.subconsultant_name}</span>}
                        {a.file_name && <span className="truncate">· {a.file_name}</span>}
                        {a.notes && <span className="truncate italic">· {a.notes}</span>}
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-primary" onClick={() => setScaPreviewDoc(a)} title="Preview" data-testid={`sca-view-${a.id}`}>
                      <Eye className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteSubconsultantAgreement(a.id)} title="Delete" data-testid={`sca-delete-${a.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Subconsultant Agreement Preview */}
      <DocumentPreviewModal open={!!scaPreviewDoc} onClose={() => setScaPreviewDoc(null)} doc={scaPreviewDoc} />

      {/* Convert to Project Dialog */}
      <Sheet open={convertOpen} onOpenChange={setConvertOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">Convert to Project</SheetTitle><SheetDescription>Select sections to include in the project</SheetDescription></SheetHeader>
          <div className="mt-4 space-y-3">
            {(selected?.sections || []).map(s => {
              const isReimbursable = s.is_default || s.section_name === 'Reimbursables';
              return (
                <div key={s.id} className="flex items-center gap-3 p-2 rounded border border-border">
                  <Checkbox
                    checked={!!sectionChecks[s.id]}
                    onCheckedChange={v => !isReimbursable && setSectionChecks({ ...sectionChecks, [s.id]: v })}
                    disabled={isReimbursable}
                    data-testid={`convert-check-${s.id}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{s.section_name}</p>
                      {isReimbursable && <Badge variant="outline" className="text-xs text-muted-foreground">Required</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">${(s.fee_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                </div>
              );
            })}
            <Button onClick={handleConvert} className="w-full bg-emerald-500 text-white hover:bg-emerald-600" data-testid="confirm-convert-btn">Create Project</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Push Sections to Project */}
      <Sheet open={pushOpen} onOpenChange={setPushOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          <SheetHeader><SheetTitle className="font-heading">Push Sections to Project</SheetTitle><SheetDescription>Select unlinked sections to add to the existing project</SheetDescription></SheetHeader>
          <div className="mt-4 space-y-3">
            {(selected?.sections || []).filter(s => !s.project_id).map(s => (
              <div key={s.id} className="flex items-center gap-3 p-2 rounded border border-border">
                <Checkbox checked={!!pushChecks[s.id]} onCheckedChange={v => setPushChecks({ ...pushChecks, [s.id]: v })} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {s.section_number && <Badge variant="outline" className="text-xs font-mono">{s.section_number}</Badge>}
                    <p className="text-sm font-medium">{s.section_name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">${(s.fee_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
              </div>
            ))}
            {(selected?.sections || []).filter(s => !s.project_id).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">All sections are already linked to the project</p>
            )}
            <Button onClick={handlePush} className="w-full bg-primary text-primary-foreground" data-testid="confirm-push-btn">Push to Project</Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Deactivation Reason Dialog */}
      <Dialog open={deactivateOpen} onOpenChange={setDeactivateOpen}>
        <DialogContent className="bg-card border-border sm:max-w-md" data-testid="deactivate-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Mark Contract Inactive</DialogTitle>
            <DialogDescription>
              {selected?.contract_number}{selected?.project_id ? '. This will also archive the linked project.' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Reason for deactivation *</Label>
            <Textarea
              value={deactivateReason}
              onChange={e => setDeactivateReason(e.target.value)}
              placeholder="e.g., Project cancelled by client, scope no longer needed..."
              className="bg-background border-border min-h-[80px]"
              data-testid="deactivate-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeactivateOpen(false)}>Cancel</Button>
            <Button
              size="sm"
              className="bg-zinc-600 text-white hover:bg-zinc-700 gap-1"
              disabled={!deactivateReason.trim()}
              onClick={async () => {
                try {
                  const r = await api.post(`/contracts/${selected.id}/deactivate`, { reason: deactivateReason.trim() });
                  toast.success('Contract deactivated' + (r.data.archived_project ? `. Project ${r.data.archived_project.project_number} archived` : ''));
                  setDeactivateOpen(false);
                  setDetailOpen(false);
                  fetchContracts();
                } catch (e) { toast.error(e.response?.data?.detail || 'Failed to deactivate'); }
              }}
              data-testid="confirm-deactivate-btn"
            >
              <Archive className="h-3 w-3" /> Confirm Deactivation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
